/**
 * auth-login.ts
 * Shared Zod contracts for POST /auth/login, account-session claims,
 * POST /auth/select-company, and POST /auth/switch-company.
 *
 * ZERO Hono/Drizzle/infrastructure imports — Zod only.
 *
 * Specs: KI-24.1, KI-24.6, KI-25.4, KC-4.3, KI-27.1, KI-28.7
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// T01 — Login request body
// ---------------------------------------------------------------------------

/**
 * POST /auth/login request body.
 * KI-24.1: email + password required; companySlug is OPTIONAL (transition field:
 * used only as a disambiguator when the account has >1 membership; never sets tenantId directly).
 */
export const LoginBodySchema = z.object({
  email: z.string().email('email must be a valid email address'),
  password: z.string().min(1, 'password is required'),
  companySlug: z
    .string()
    .regex(/^[a-z0-9-]+$/, 'companySlug must contain only lowercase letters, digits, and hyphens')
    .max(100, 'companySlug must be at most 100 characters')
    .optional(),
});

export type LoginBody = z.infer<typeof LoginBodySchema>;

// ---------------------------------------------------------------------------
// T01 — Login response: discriminated union on `kind`
// ---------------------------------------------------------------------------

/**
 * Single-membership (or slug-disambiguated) response.
 * KI-24.6: byte-compatible with the pre-change { token } response — old SPA code that
 * reads `.token` directly still works for single-membership users.
 */
export const LoginTokenResponseSchema = z.object({
  kind: z.literal('token'),
  token: z.string(),
});

export type LoginTokenResponse = z.infer<typeof LoginTokenResponseSchema>;

/**
 * Multi-membership response (no slug disambiguator matched).
 * KI-24.6: returns a short-lived account-session token + the list of companies so
 * the client can show a company picker and call POST /auth/select-company.
 */
export const LoginChooseResponseSchema = z.object({
  kind: z.literal('choose-company'),
  /** Short-lived account-session JWT (scope:'account'). Accepted only by /auth/select-company. */
  accountSession: z.string(),
  /** Membership-scoped companies this account can access. */
  companies: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      slug: z.string(),
    }),
  ),
});

export type LoginChooseResponse = z.infer<typeof LoginChooseResponseSchema>;

/**
 * Discriminated union on `kind`.
 * KI-24.6: parse both login response shapes from the same schema.
 */
export const LoginResponseSchema = z.discriminatedUnion('kind', [
  LoginTokenResponseSchema,
  LoginChooseResponseSchema,
]);

export type LoginResponse = z.infer<typeof LoginResponseSchema>;

// ---------------------------------------------------------------------------
// T02 — Account-session JWT claims
// ---------------------------------------------------------------------------

/**
 * Claims shape for the short-lived account-session token (scope:'account').
 * KC-4.3: this token MUST NOT carry tenantId or roles — it is a proof of
 * "password verified for this account", not a tenant-scoped session.
 * The auth middleware validates scope/sub/exp and forbids tenantId when scope='account'.
 */
export const AccountClaimsSchema = z.object({
  scope: z.literal('account'),
  /** The global account.id (NOT the membership/app_user id). */
  sub: z.string().uuid(),
  exp: z.number(),
  iat: z.number(),
});

export type AccountClaims = z.infer<typeof AccountClaimsSchema>;

// ---------------------------------------------------------------------------
// T02 — select-company and switch-company request bodies
// ---------------------------------------------------------------------------

/**
 * POST /auth/select-company request body.
 * KI-27.1: requires an account-session token in the Authorization header.
 * companyId must be a valid UUID; the backend verifies the account has an active
 * membership in this company before issuing a tenant token.
 */
export const SelectCompanyBodySchema = z.object({
  companyId: z.string().uuid('companyId must be a valid UUID'),
});

export type SelectCompanyBody = z.infer<typeof SelectCompanyBodySchema>;

/**
 * POST /auth/switch-company request body.
 * KI-28.7: requires a valid tenant token in the Authorization header.
 * targetCompanyId must be a valid UUID; the backend verifies the account has an active
 * membership in the target company before issuing a fresh tenant token.
 * The issued tenantId is ALWAYS the DB-resolved company.id of the verified membership —
 * never the raw targetCompanyId from the body (KC-3.5 / design §3.2).
 */
export const SwitchCompanyBodySchema = z.object({
  targetCompanyId: z.string().uuid('targetCompanyId must be a valid UUID'),
});

export type SwitchCompanyBody = z.infer<typeof SwitchCompanyBodySchema>;
